// The scene-reel request body (API contract §2) — ONE builder, shared by the
// Step 4 dry-run preview and buildBody, so the free preview and the billable
// request can never describe different reels, and a duplicate-confirm retry
// stays byte-identical apart from its two override fields (the backend
// fingerprints the request to decide whether it is the same one).
//
// Built here rather than by editing Step4Generate's standard literal: that
// literal is fingerprinted too, and every standard request must keep hashing
// exactly as it did. The fields a scene reel ignores (platformFormat, kinds,
// staticFormats, videoFormats, expandStaticFormats, videoDurationSec,
// mediaIds, excludePairings, include*Matched) are simply not sent, and the
// fields it REFUSES (videoPromptRaw, videoPromptGuidance, directorVariants —
// SCENE_REEL_FIELD_UNSUPPORTED) are never sent either: the wizard hides those
// controls in scene-reel mode, so a leftover draft must not ride along.

import type { SeedPick, WizardSelections } from './index';
import type { WorkflowEntry } from './formatCatalog';
import type { SceneReelBrandAccess } from '../../brand/useAdReadiness';
import { isApiError } from '../../auth/apiFetch';

export type SceneReelRequestBody = {
  preset:      'scene_reel';
  campaignId:  string | null;
  productIds:  string[];
  templateIds: string[];
  cta:         { text: string; url: string };
  urlParams:   string;
  sceneModel?: string;
  seedPicks:   SeedPick[];
};

export type SceneReelGateReason =
  | 'unavailable'
  | 'brand-not-allowed'
  | 'needs-product'
  | 'too-many-products';

// The backend's refusal for a brand that is not on SCENE_REEL_BRAND_ALLOWLIST
// (API contract §2: 403 on /preview and /generate, judged on the campaign's
// brand; §6 on a scene regenerate).
export const SCENE_REEL_BRAND_NOT_ALLOWED = 'SCENE_REEL_BRAND_NOT_ALLOWED';

const BRAND_NOT_ALLOWED_MESSAGE =
  'Scene reels are not turned on for this brand yet. Switch back to standard ads to generate for it.';

/**
 * Whether this brand may make a scene reel: GET /api/brand/:id/ad-readiness
 * `sceneReel.allowed` (contract §2a). Only a literal true counts — absent
 * (workflow off, older backend, still loading) and false both hide it.
 */
export function sceneReelBrandAllowed(access: SceneReelBrandAccess | null | undefined): boolean {
  return access?.allowed === true;
}

/**
 * The operator-facing line for a scene-reel refusal the wizard knows by code,
 * or null to show the backend's own message. Keyed on `code`, never on the
 * text, so a reworded backend message cannot turn it back into a bare error.
 */
export function sceneReelRefusalMessage(err: unknown): string | null {
  if (!isApiError(err)) return null;
  const code = (err.body as { code?: unknown } | null)?.code;
  return code === SCENE_REEL_BRAND_NOT_ALLOWED ? BRAND_NOT_ALLOWED_MESSAGE : null;
}

/**
 * Preferred scene images actually sent: only for products in this run, each
 * pair once, at most `perProduct` per product, in pick (= scene) order. The
 * picker already enforces all three; this keeps a stale pick from turning a
 * valid run into a 400 SCENE_REEL_BAD_PICKS.
 */
export function sceneSeedPicksFor(
  productIds: string[],
  picks: SeedPick[] | null | undefined,
  perProduct: number
): SeedPick[] {
  const live = new Set(productIds.map(String));
  const seen = new Set<string>();
  const counts = new Map<string, number>();
  const out: SeedPick[] = [];
  for (const p of picks || []) {
    const productId = String(p.productId);
    const mediaId = String(p.mediaId);
    if (!live.has(productId) || !mediaId) continue;
    const key = `${productId}|${mediaId}`;
    if (seen.has(key)) continue;
    const n = (counts.get(productId) || 0) + 1;
    if (n > perProduct) continue;
    seen.add(key);
    counts.set(productId, n);
    out.push({ productId, mediaId });
  }
  return out;
}

export function sceneReelRequestBody(
  value: Pick<WizardSelections,
    'campaignId' | 'productIds' | 'templateIds' | 'ctaText' | 'ctaUrl' | 'urlParams' |
    'sceneModel' | 'sceneSeedPicks'>,
  maxSeedPicksPerProduct = 3
): SceneReelRequestBody {
  return {
    preset:      'scene_reel',
    campaignId:  value.campaignId,
    productIds:  value.productIds,
    templateIds: value.templateIds,
    cta:         { text: value.ctaText, url: value.ctaUrl },
    urlParams:   value.urlParams,
    // Omitted rather than guessed when nothing is chosen yet: the backend
    // resolves an absent model to its default and hashes the resolved key.
    ...(value.sceneModel ? { sceneModel: value.sceneModel } : {}),
    seedPicks:   sceneSeedPicksFor(value.productIds, value.sceneSeedPicks, maxSeedPicksPerProduct)
  };
}

/**
 * Why Generate is off in scene-reel mode, or null when it may run. Replaces
 * the standard "pick at least one size" gate, which does not apply: a scene
 * reel ignores the ticked surfaces. Mirrors the backend's own refusals
 * (SCENE_REEL_DISABLED / BRAND_NOT_ALLOWED / NEEDS_PRODUCTS /
 * TOO_MANY_PRODUCTS).
 */
export function sceneReelGate(
  productCount: number,
  workflow: WorkflowEntry | null,
  brandAccess: SceneReelBrandAccess | null | undefined
): SceneReelGateReason | null {
  if (!workflow) return 'unavailable';
  if (!sceneReelBrandAllowed(brandAccess)) return 'brand-not-allowed';
  if (productCount < 1) return 'needs-product';
  if (productCount > workflow.maxProducts) return 'too-many-products';
  return null;
}

export function sceneReelGateMessage(
  reason: SceneReelGateReason,
  productCount: number,
  workflow: WorkflowEntry | null
): string {
  switch (reason) {
    case 'unavailable':
      return 'Scene reels are not available right now. Switch back to standard ads.';
    case 'brand-not-allowed':
      return BRAND_NOT_ALLOWED_MESSAGE;
    case 'needs-product':
      return 'Pick at least one product to make a scene reel. Media-only runs are not supported.';
    case 'too-many-products': {
      const max = workflow?.maxProducts ?? 1;
      return `A scene reel run takes at most ${max} product${max === 1 ? '' : 's'} — ${productCount} selected.`;
    }
  }
}
